import { api } from './client';
import { compressImage } from '@/lib/compressImage';

export interface Item {
  id: number;
  code: string;
  name: string;
  barcode: string | null;
  category: string;
  unit: string;
  hsn: string | null;
  gstPct: string;
  width: string | null;
  length: string | null;
  sizeUnit: string | null;
  gsm: number | null;
  color: string | null;
  style: string | null;
  collection: string | null;
  brand: string | null;
  purchaseRate: string;
  additionalCost: string | null;
  marginPct: string | null;
  mrp: string;
  distDiscount: string | null;
  distPrice: string | null;
  dealerDiscount: string | null;
  dealerPrice: string | null;
  reorderLevel: number | null;
  netWeightKg: string | null;
  grossWeightKg: string | null;
  preferredVendorId: number | null;
  description: string | null;
  imageUrl: string | null;
  status: 'Active' | 'Inactive';
  warehouseStock: { currentQty: string; openingQty: string; reorderLevel: number | null } | null;
  openingSerials?: string[];
}

export interface ItemListResult {
  data: Item[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ItemFilter {
  search?: string;
  category?: string;
  collection?: string;
  status?: 'Active' | 'Inactive';
  /** Sorts the whole list on the server (default: item name A→Z). */
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export async function listItems(filter: ItemFilter = {}): Promise<ItemListResult> {
  const r = await api.get<ItemListResult>('/items', { params: filter });
  return r.data;
}

/** Distinct categories that belong to items — for the Item Master filter dropdown. */
export async function fetchItemCategories(): Promise<string[]> {
  const r = await api.get<string[]>('/items/categories');
  return r.data;
}

/** Distinct collections that belong to items — for the Item Master filter dropdown. */
export async function fetchItemCollections(): Promise<string[]> {
  const r = await api.get<string[]>('/items/collections');
  return r.data;
}

export async function getItem(id: number): Promise<Item> {
  const r = await api.get<Item>(`/items/${id}`);
  return r.data;
}

export async function createItem(payload: Partial<Item>): Promise<Item> {
  const r = await api.post<Item>('/items', payload);
  return r.data;
}

export async function updateItem(id: number, payload: Partial<Item>): Promise<Item> {
  const r = await api.patch<Item>(`/items/${id}`, payload);
  return r.data;
}

export async function deleteItem(id: number): Promise<void> {
  await api.delete(`/items/${id}`);
}

export async function deleteAllItems(): Promise<{ deleted: number }> {
  const r = await api.delete<{ deleted: number }>('/items/all');
  return r.data;
}

export async function uploadItemImage(id: number, file: File): Promise<Item> {
  // Shrink first — item images print on POs, GRNs, invoices and most other documents.
  const small = await compressImage(file);
  const fd = new FormData();
  fd.append('image', small, small instanceof File ? small.name : file.name);
  const r = await api.post<Item>(`/items/${id}/image`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
}

export async function deleteItemImage(id: number): Promise<Item> {
  const r = await api.delete<Item>(`/items/${id}/image`);
  return r.data;
}

export function itemsExportPath(filter: ItemFilter = {}): string {
  const search = new URLSearchParams(
    Object.fromEntries(Object.entries(filter).filter(([, v]) => v != null && v !== '')) as Record<string, string>
  ).toString();
  return `/items/export.xlsx${search ? `?${search}` : ''}`;
}

export function itemsImportTemplatePath(): string {
  return '/items/import-template.xlsx';
}

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

export async function importItems(file: File): Promise<ImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post<ImportResult>('/items/import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60 * 1000, // 5 minutes — large files with many rows can be slow
  });
  return r.data;
}
